import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import moment from 'moment';

import AdjustRemarkTextarea from 'components/admin/AdjustRemarkTextarea';
import TicketOtherInformationField from 'components/admin/TicketOtherInformationField';
import BaseMultipleSelectorV2 from 'components/base/BaseMultipleSelectorV2';
import CustomRadios from 'components/base/CustomRadios';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import ErrorFieldMessage from 'components/base/ErrorFieldMessage';
import BasePrompt from 'components/base/prompt/BasePrompt';
import { CustomTitleWithDropDown } from 'components/customer/CustomTitleWithDropDown';
import MembershipInfoCard from 'components/customer/MembershipInfoCard';
import AdjustValidationResult from 'components/customerDetail/AdjustValidationResult';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import {
  ADDITIONAL_FIELD_FORMAT_TYPE,
  ADJUST_TYPES,
  COUPON_TYPES,
  CUSTOMER_FIELD_NAME_LABELS,
  CUSTOM_ADDITIONAL_FIELD_FORMAT_TYPE,
  CheckStatus,
  SavedStatus,
  TICKET_CAMPAIGN_OTHER_INFORMATION_QUESTION_TYPE,
} from 'config/CustomEnums';
import { TRANSACTION_RECORD_TYPE } from 'containers/record/records/TransactionList';
import { ToastType, defaultDelayTime } from 'models/NavBarModel';
import { selectStore } from 'services/CouponAPIHelper';

import { createAction } from 'utils';
import { TimeFormater } from 'utils/TimeFormatUtil';

import './AdjustPrompt.scss';

const PurposeTypes = {
  add: 'Add',
  remove: 'Remove',
};
const StoreSelectionTypes = {
  customerSelect: { label: 'Customer select', value: 'CUSTOMER_SELECT' },
  selectNow: { label: 'Select now', value: 'SELECT_NOW' },
};

const getRevertOption = (value, options) => {
  const optionKey = Object.keys(options).filter(
    (item) => options[item].value === value,
  )[0];
  return options[optionKey] || {};
};

function AdjustPrompt({
  show,
  setShow,
  adjustType,
  prefillData = {},
  customer = null,
}) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    storeList,
    transactionList,
    couponTemplateList,
    campaignList,
    customerList,
    customerDetail,
    ownedCoupons,
    adjustValidationResult,
  } = useSelector((state) => ({
    storeList: state.storeModel?.storeList,
    transactionList: state.transactions?.transactionList,
    couponTemplateList: state.couponList?.couponTemplateList,
    campaignList: state.campaignList?.allList,
    customerList: state.customerList?.customerList,
    customerDetail: state.customerList?.customer,
    ownedCoupons: state.singleCoupon?.notPagedAllList,
    adjustValidationResult: state.customerList?.adjustValidationResult,
  }));

  const [targetCustomer, setTargetCustomer] = useState();
  const [targetCouponTemplate, setTargetCouponTemplate] = useState();
  const [targetStore, setTargetStore] = useState();
  const [targetTransaction, setTargetTransaction] = useState();
  const [targetCampaign, setTargetCampaign] = useState();
  const [purpose, setPurpose] = useState(PurposeTypes.add);
  const [storeSelectionType, setStoreSelectionType] = useState(
    StoreSelectionTypes.customerSelect.value,
  );
  const [otherInformations, setOtherInfomations] = useState([]);
  const [quantity, setQuantity] = useState();
  const [removedCoupons, setRemovedCoupons] = useState([]);
  const [remarks, setRemarks] = useState('');

  const [refreshDate, setRefreshDate] = useState(new Date());
  const [storeRefreshDate, setStoreRefreshDate] = useState(new Date());
  const [lastStoreRefreshDate, setLastStoreRefreshDate] =
    useState(storeRefreshDate);

  const [customerRefreshDate, setCustomerRefreshDate] = useState(new Date());
  const [lastCustomerRefreshDate, setLastCustomerRefreshDate] =
    useState(customerRefreshDate);
  const [campaignRefreshDate, setCampaignRefreshDate] = useState(new Date());
  const [lastCampaignRefreshDate, setLastCampaignRefreshDate] =
    useState(campaignRefreshDate);
  const [couponTemplateRefreshDate, setCouponTemplateRefreshDate] = useState(
    new Date(),
  );
  const [lastCouponTemplateRefreshDate, setLastCouponTemplateRefreshDate] =
    useState(couponTemplateRefreshDate);
  const [transactionRefreshDate, setTransactionRefreshDate] = useState(
    new Date(),
  );
  const [lastTransactionRefreshDate, setLastTransactionRefreshDate] = useState(
    transactionRefreshDate,
  );

  const [error, setError] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [manualDisableButton, setManualDisableButton] = useState(false);
  const [showAddConfirm, setShowAddConfirm] = useState(false);

  useEffect(() => {
    dispatch(
      createAction('customerList/getPagedCustomerAdditionalDataFields')({
        pageSize: 100,
        queryDetail: true,
      }),
    );
  }, []);

  useEffect(() => {
    if (show) {
      const prefillCustomer = customer || prefillData?.targetCustomer || null;
      setTargetCustomer(prefillCustomer);
      setTargetCouponTemplate(prefillData?.targetCouponTemplate || null);
      setTargetStore();
      setTargetTransaction();
      setTargetCampaign(prefillData?.targetCampaign || null);
      setPurpose(prefillData?.purpose || PurposeTypes.add);
      setStoreSelectionType(StoreSelectionTypes.customerSelect.value);
      setOtherInfomations([]);
      setQuantity();
      setRemovedCoupons([]);
      setRemarks('');
      setError({});
      setConfirm(false);
      setShowAddConfirm(false);
      setManualDisableButton(false);

      if (
        [ADJUST_TYPES.coupon, ADJUST_TYPES.ticket].includes(adjustType) &&
        prefillCustomer?.pk
      ) {
        dispatch({ type: 'singleCoupon/clearNotPagedAllList' });
        dispatch(
          createAction('singleCoupon/getAllList')({
            isSimpleList: true,
            customerOwned: prefillCustomer?.pk,
            isActive: true,
            isUsedOrExpired: false,
            couponType:
              adjustType === ADJUST_TYPES.ticket
                ? COUPON_TYPES.TICKET
                : COUPON_TYPES.NORMAL,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (
      show &&
      (customerDetail?.pk === targetCustomer?.pk || !targetCustomer?.pk)
    ) {
      setTargetCustomer(customerDetail);
    }
  }, [customerDetail]);

  useEffect(() => {
    if (
      show &&
      adjustType === ADJUST_TYPES.stamp &&
      prefillData?.targetCampaign?.pk &&
      campaignList?.length
    ) {
      const campaign =
        campaignList.filter(
          (item) => item.pk === prefillData?.targetCampaign?.pk,
        )[0] || null;
      if (campaign) {
        setTargetCampaign(campaign);
      }
    }
  }, [campaignList]);

  const validators = {
    [ADJUST_TYPES.coupon]: {
      [PurposeTypes.add]: () => {
        if (
          !targetCustomer?.pk ||
          !targetCouponTemplate?.pk ||
          (storeSelectionType === StoreSelectionTypes.selectNow.value &&
            !targetStore?.pk) ||
          !quantity
        ) {
          return true;
        }
        return false;
      },
      [PurposeTypes.remove]: () => {
        if (!targetCustomer?.pk || !removedCoupons?.length) {
          return true;
        }
        return false;
      },
    },
    [ADJUST_TYPES.ticket]: {
      [PurposeTypes.add]: () => {
        if (
          !targetCustomer?.pk ||
          !targetCampaign?.pk ||
          !targetCouponTemplate?.pk ||
          !quantity ||
          (targetCampaign?.fillOtherInfomation &&
            targetCampaign?.otherInformations?.filter((itemData, index) => {
              const { fieldFormat, isOptional } = itemData;
              return (
                !isOptional &&
                !otherInformations[index] &&
                ![
                  ADDITIONAL_FIELD_FORMAT_TYPE.TOGGLE,
                  ADDITIONAL_FIELD_FORMAT_TYPE.CHECKBOX,
                ].includes(fieldFormat)
              );
            }).length)
        ) {
          return true;
        }
        return false;
      },
      [PurposeTypes.remove]: () => {
        if (!targetCustomer?.pk || !removedCoupons?.length) {
          return true;
        }
        return false;
      },
    },
    [ADJUST_TYPES.stamp]: {
      [PurposeTypes.add]: () => {
        if (!targetCampaign?.pk || !quantity) {
          return true;
        }
        return false;
      },
      [PurposeTypes.remove]: () => {
        if (!targetCampaign?.pk || !quantity) {
          return true;
        }
        return false;
      },
    },
  };

  const getConfirmButtonDisabledStatus = () => {
    if (confirm) {
      return false;
    }
    console.log('@values', {
      targetCustomer,
      purpose,
      targetCouponTemplate,
      storeSelectionType,
      targetStore,
      quantity,
      removedCoupons,
      targetTransaction,
      targetCampaign,
      otherInformations,
      remarks,
    });
    return validators[adjustType]?.[purpose]();
  };

  const disabledButton =
    getConfirmButtonDisabledStatus() || manualDisableButton;

  const purposePreview = () => {
    return (
      <>
        <label className="custom-record-normal-font custom-timeline-time-color list-new-line">
          {purpose} {adjustType}s
        </label>
      </>
    );
  };
  const removeOwnedCouponsPreview = () => {
    let removedCouponsFormated = {};
    if (removedCoupons?.length) {
      const temp = removedCoupons.forEach((item) => {
        const coupon = item;
        if (!removedCouponsFormated[coupon?.templatePK]) {
          removedCouponsFormated[coupon?.templatePK] = {
            name: null,
            coupons: [],
            storeNames: [],
          };
        }
        let storeNames =
          removedCouponsFormated[coupon?.templatePK]['storeNames'] || [];
        if (coupon?.storeName && !storeNames.includes(coupon?.storeName)) {
          storeNames.push(coupon?.storeName);
        }
        removedCouponsFormated[coupon?.templatePK] = {
          name: coupon?.templateName,
          coupons: [
            ...(removedCouponsFormated[coupon?.templatePK]['coupons'] || []),
            coupon,
          ],
          storeNames: storeNames,
        };
      });
    }
    console.log('@removedCouponsFormated', removedCouponsFormated);
    return (
      <>
        <ul className="owned-coupons-value-container">
          {Object.keys(removedCouponsFormated)?.map((templatePK) => {
            return (
              <>
                <li className="owned-coupon-item">
                  {`[${
                    removedCouponsFormated[templatePK]['coupons']?.length || 0
                  }] [ID: ${templatePK}] ${
                    removedCouponsFormated[templatePK]['name']
                  }`}
                  <ul className="owned-coupon-stores-container">
                    {removedCouponsFormated[templatePK]['storeNames'].map(
                      (storeName) => {
                        return (
                          <li className="owned-coupon-store">{storeName}</li>
                        );
                      },
                    )}
                  </ul>
                </li>
              </>
            );
          })}
        </ul>
      </>
    );
  };

  const otherBody = () => {
    if (showAddConfirm) {
      return (
        <AdjustValidationResult
          adjustValidationResult={adjustValidationResult}
        />
      );
    }
    if (confirm) {
      let data = {
        targetCustomer: targetCustomer?.pk,
        purpose,
        targetCouponTemplate: targetCouponTemplate?.title,
        storeSelectionType: getRevertOption(
          storeSelectionType,
          StoreSelectionTypes,
        )?.label,
        targetStore: targetStore?.pk
          ? `[ID: ${targetStore.pk}] ${targetStore.name}`
          : null,
        quantity,
        removedCoupons,
        removedCouponsCount: removedCoupons?.length || 0,
        targetTransaction,
        targetCampaign: targetCampaign?.pk
          ? `[ID: ${targetCampaign.pk}] ${targetCampaign.name}`
          : targetCampaign?.name,
        remarks,
      };
      let fields = [];
      let otherInformationFields = [];
      if (adjustType === ADJUST_TYPES.coupon) {
        if (purpose === PurposeTypes.add) {
          fields = [
            [
              {
                title: 'Purpose',
                customRender: purposePreview(),
              },
            ],
            [{ title: 'Store selection', field: 'storeSelectionType' }],
          ];
          if (storeSelectionType === StoreSelectionTypes.selectNow.value) {
            fields.push([{ title: 'Store', field: 'targetStore' }]);
          }
          fields = [
            ...fields,
            [{ title: 'Coupon quantity', field: 'quantity' }],
            [{ title: 'Remark (optional)', field: 'remarks' }],
          ];
        } else {
          fields = [
            [{ title: 'Remove coupon quantity', field: 'removedCouponsCount' }],
            [
              {
                title: 'Owned coupons',
                customRender: removeOwnedCouponsPreview(),
              },
            ],
            [{ title: 'Remark (optional)', field: 'remarks' }],
          ];
        }
      } else if (adjustType === ADJUST_TYPES.ticket) {
        if (purpose === PurposeTypes.add) {
          fields = [
            [
              {
                title: 'Purpose',
                customRender: purposePreview(),
              },
            ],
            [{ title: 'Ticket campaign', field: 'targetCampaign' }],
            [{ title: 'Time Slot', field: 'targetCouponTemplate' }],
            [{ title: 'Ticket quantity', field: 'quantity' }],
          ];

          if (
            targetCampaign?.fillOtherInfomation &&
            targetCampaign?.otherInformations?.length
          ) {
            let formatedOtherInformations = {};
            const temp = targetCampaign?.otherInformations?.forEach(
              (itemData, index) => {
                const { fieldLabel, fieldFormat, isOptional, questionType } =
                  itemData;
                let datetimeFormat = TimeFormater.dayMonthYearWeekTime;
                if (
                  questionType ===
                  TICKET_CAMPAIGN_OTHER_INFORMATION_QUESTION_TYPE.CUSTOMER_PROFILE_FIELD
                ) {
                  if (CUSTOMER_FIELD_NAME_LABELS.BIRTHDAY === fieldLabel) {
                    datetimeFormat = 'MMM, yyyy';
                  }
                }

                let otherInformationValue = null;
                if (
                  [
                    ADDITIONAL_FIELD_FORMAT_TYPE.TEXT,
                    ADDITIONAL_FIELD_FORMAT_TYPE.NUMBER,
                    ADDITIONAL_FIELD_FORMAT_TYPE.SINGLE_CHOICES,
                    ADDITIONAL_FIELD_FORMAT_TYPE.MULTI_CHOICES,
                  ].includes(fieldFormat)
                ) {
                  otherInformationValue = otherInformations[index];
                } else if (
                  [
                    CUSTOM_ADDITIONAL_FIELD_FORMAT_TYPE.DATE_MONTH,
                    ADDITIONAL_FIELD_FORMAT_TYPE.DATETIME,
                  ].includes(fieldFormat)
                ) {
                  otherInformationValue = otherInformations[index]
                    ? moment(otherInformations[index]).format(datetimeFormat)
                    : null;
                } else if (
                  fieldFormat === ADDITIONAL_FIELD_FORMAT_TYPE.DROPDOWN
                ) {
                  otherInformationValue = otherInformations[index]?.label;
                } else if (
                  [
                    ADDITIONAL_FIELD_FORMAT_TYPE.TOGGLE,
                    ADDITIONAL_FIELD_FORMAT_TYPE.CHECKBOX,
                  ].includes(fieldFormat)
                ) {
                  otherInformationValue = otherInformations[index]
                    ? 'Yes'
                    : 'No';
                }

                const keyField = `otherInformations_${index}`;
                formatedOtherInformations[keyField] = otherInformationValue;
                otherInformationFields.push([
                  {
                    title: isOptional ? `${fieldLabel} (Optional)` : fieldLabel,
                    field: keyField,
                  },
                ]);
              },
            );
            data = {
              ...data,
              ...formatedOtherInformations,
            };
          }
        } else {
          fields = [
            [{ title: 'Remove ticket quantity', field: 'removedCouponsCount' }],
            [
              {
                title: 'Select ticket',
                customRender: removeOwnedCouponsPreview(),
              },
            ],
            [{ title: 'Remark (optional)', field: 'remarks' }],
          ];
        }
      } else if (adjustType === ADJUST_TYPES.stamp) {
        if (purpose === PurposeTypes.add) {
          fields = [
            [
              {
                title: 'Purpose',
                customRender: purposePreview(),
              },
            ],
            [{ title: 'Stamp campaign', field: 'targetCampaign' }],
            [{ title: 'Stamp quantity', field: 'quantity' }],
            [{ title: 'Remark (optional)', field: 'remarks' }],
          ];
        } else {
          fields = [
            [
              {
                title: 'Purpose',
                customRender: purposePreview(),
              },
            ],
            [{ title: 'Stamp campaign', field: 'targetCampaign' }],
            [{ title: 'Stamp quantity', field: 'quantity' }],
            [{ title: 'Remark (optional)', field: 'remarks' }],
          ];
        }
      }

      console.log('confirmed data', data);
      return (
        <>
          <div className="adjust-other-body-preview">
            <MembershipInfoCard
              title=""
              hideTitle={true}
              data={data}
              fields={fields}
            />
            {adjustType === ADJUST_TYPES.ticket ? (
              <>
                {purpose === PurposeTypes.add ? (
                  <>
                    {targetCampaign?.fillOtherInfomation &&
                    targetCampaign?.otherInformations?.length ? (
                      <>
                        <div className="line"></div>
                        <div className="other-information-container">
                          <CustomTitleLabel title="Other information" />
                          <MembershipInfoCard
                            title="Other information"
                            hideTitle={true}
                            data={data}
                            fields={otherInformationFields}
                          />
                        </div>
                        <div className="line"></div>
                      </>
                    ) : null}
                    <MembershipInfoCard
                      title=""
                      hideTitle={true}
                      data={data}
                      fields={[
                        [{ title: 'Remark (optional)', field: 'remarks' }],
                      ]}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </>
      );
    }
    return (
      <>
        <div className="adjust-other-body">
          {customer ? null : (
            <>
              <CustomTitleWithDropDown
                title="Customer"
                defaultValue={{
                  value: targetCustomer,
                  label: targetCustomer?.pk
                    ? `[ID: ${targetCustomer?.pk}] ${targetCustomer?.name}`
                    : targetCustomer?.name,
                }}
                setValue={(item) => {
                  if (item?.value?.pk != targetCustomer?.pk) {
                    if (adjustType === ADJUST_TYPES.stamp) {
                      if (!prefillData?.targetCampaign?.pk) {
                        setTargetCampaign();
                      } else {
                        dispatch({
                          type: 'campaignList/getCampaignList',
                          payload: {
                            noLoading: true,
                            isSelectorLoad: true,
                            isStamp: true,
                            ssoUid: item?.value?.ssoUid,
                            customerIdSkip: item?.value?.pk,
                            reverse: true,
                            skipUpdatePagedData: true,
                            campaignIds: [prefillData?.targetCampaign?.pk],
                          },
                        });
                      }
                    } else {
                      setTargetTransaction();
                    }
                  }
                  setTargetCustomer(item?.value);
                  if (adjustType === ADJUST_TYPES.stamp) {
                    if (!prefillData?.targetCampaign?.pk) {
                      setTargetCampaign();
                    }
                  }
                  if (adjustType !== ADJUST_TYPES.ticket) {
                    setLastTransactionRefreshDate(new Date());
                  }
                  if (adjustType !== ADJUST_TYPES.coupon) {
                    setLastCampaignRefreshDate(new Date());
                  }
                  if (item?.value?.pk) {
                    dispatch({
                      type: 'customerList/getOneCustomer',
                      payload: { id: item?.value?.pk },
                    });
                    if (
                      [ADJUST_TYPES.coupon, ADJUST_TYPES.ticket].includes(
                        adjustType,
                      )
                    ) {
                      dispatch({ type: 'singleCoupon/clearNotPagedAllList' });
                      dispatch(
                        createAction('singleCoupon/getAllList')({
                          isSimpleList: true,
                          customerOwned: item?.value?.pk,
                          isActive: true,
                          isUsedOrExpired: false,
                          couponType:
                            adjustType === ADJUST_TYPES.ticket
                              ? COUPON_TYPES.TICKET
                              : COUPON_TYPES.NORMAL,
                        }),
                      );
                    }
                  }
                }}
                labelContainPk={true}
                source={customerList || []}
                loadMoreAction={'customerList/getPagedCustomers'}
                filterAction={'customerList/getPagedCustomers'}
                defaultFilter={{
                  isDropdownNode: true,
                  campaign:
                    prefillData?.targetCampaign?.pk || targetCampaign?.pk,
                  status:
                    'active,temporary_earning,temporary,prospect,deactive,delete_requested',
                }}
                isReset={lastCustomerRefreshDate !== customerRefreshDate}
                resetChange={() => {
                  setLastCustomerRefreshDate(customerRefreshDate);
                }}
                hideDeleteButton={true}
                placeholder={'Search by customer ID and name'}
              />
            </>
          )}

          <CustomTitleLabel title="Purpose" />
          <div style={{ display: 'flex' }}>
            <CustomRadios
              className="d-flex"
              onChange={(value) => {
                if (value !== purpose) {
                  setError({});
                  if (adjustType === ADJUST_TYPES.stamp) {
                    setLastCampaignRefreshDate(new Date());
                  }
                }
                setPurpose(value);
              }}
              default={purpose}
              options={[
                {
                  label: `${PurposeTypes.add} ${adjustType}s`,
                  value: PurposeTypes.add,
                },
                {
                  label: `${PurposeTypes.remove} ${adjustType}s`,
                  value: PurposeTypes.remove,
                },
              ]}
            />
          </div>

          {adjustType === ADJUST_TYPES.coupon ? (
            <>
              {purpose === PurposeTypes.add ? (
                <>
                  {customer?.pk || !prefillData?.targetCouponTemplate?.pk ? (
                    <>
                      <CustomTitleWithDropDown
                        title="Select coupon set"
                        defaultValue={{
                          value: targetCouponTemplate,
                          label: targetCouponTemplate?.pk
                            ? `[ID: ${targetCouponTemplate?.pk}] ${targetCouponTemplate?.name}`
                            : targetCouponTemplate?.name,
                        }}
                        setValue={(item) => {
                          console.log('couponChange:', item);
                          if (item?.value?.pk !== targetCouponTemplate?.pk) {
                            setTargetStore();
                          }
                          setTargetCouponTemplate(item?.value);
                          dispatch(
                            createAction('storeModel/getPagedStoreList')({
                              isSelectorLoad: true,
                              couponTemplate: item?.value?.pk,
                              active: 'true',
                            }),
                          );
                        }}
                        source={couponTemplateList || []}
                        labelContainPk
                        loadMoreAction={'couponList/getCurrentPageTemplateList'}
                        filterAction={'couponList/getCurrentPageTemplateList'}
                        defaultFilter={{
                          type: COUPON_TYPES.NORMAL,
                          searchField: 'searchNameOrId',
                          isSimpleWithStock: true,
                        }}
                        isReset={
                          lastCouponTemplateRefreshDate !==
                          couponTemplateRefreshDate
                        }
                        resetChange={() => {
                          setLastCouponTemplateRefreshDate(
                            couponTemplateRefreshDate,
                          );
                        }}
                        hideDeleteButton={true}
                        placeholder={'Search by ID,  name'}
                      />
                    </>
                  ) : null}
                  {targetCouponTemplate?.pk ? (
                    <>
                      {targetCouponTemplate?.shouldBeUsedInStores ? (
                        <>
                          <CustomTitleLabel title="Store selection" />
                          <div style={{ display: 'flex' }}>
                            <CustomRadios
                              className="d-flex"
                              onChange={(value) => {
                                setStoreSelectionType(value);
                              }}
                              default={storeSelectionType}
                              options={[
                                StoreSelectionTypes.customerSelect,
                                StoreSelectionTypes.selectNow,
                              ]}
                            />
                          </div>
                          {storeSelectionType ===
                          StoreSelectionTypes.selectNow.value ? (
                            <>
                              <CustomTitleWithDropDown
                                customClass={'store-selector'}
                                title="Store"
                                defaultValue={{
                                  value: targetStore,
                                  label: targetStore?.pk
                                    ? `${
                                        targetStore?.isOutOfStock
                                          ? '[Out of stock] - '
                                          : ''
                                      }[ID: ${targetStore?.pk}] ${
                                        targetStore?.name
                                      }`
                                    : targetStore?.name,
                                }}
                                setValue={(item) => {
                                  setTargetStore(item?.value);
                                }}
                                source={storeList || []}
                                customItem={(item) => {
                                  return `${
                                    item.isOutOfStock ? '[Out of stock] - ' : ''
                                  }[ID: ${item.pk}] ${item.name}`;
                                }}
                                showItemDescription={true}
                                customItemDescription={(item) =>
                                  item.storeAddress
                                }
                                customItemDisabled={(item) => item.isOutOfStock}
                                loadMoreAction={'storeModel/getPagedStoreList'}
                                filterAction={'storeModel/getPagedStoreList'}
                                defaultFilter={{
                                  couponTemplate: targetCouponTemplate?.pk,
                                  searchField: 'searchNameOrId',
                                  active: 'true',
                                }}
                                isReset={
                                  lastStoreRefreshDate !== storeRefreshDate
                                }
                                resetChange={() => {
                                  setLastStoreRefreshDate(storeRefreshDate);
                                }}
                                hideDeleteButton={true}
                                placeholder={'Search by ID,  name'}
                              />
                            </>
                          ) : null}
                        </>
                      ) : null}
                      <CustomTitleWithInput
                        title={'Coupon quantity'}
                        defaultValue={quantity}
                        setValue={(value) => {
                          setQuantity(value);
                        }}
                        type={'number'}
                        extra={{
                          min: 1,
                        }}
                        error={{ error: error?.quantity }}
                      />
                      {error.quantity ? (
                        <ErrorFieldMessage
                          id={'quantity'}
                          error={true}
                          message={
                            'The input value is larger than the number of coupon left, please try again.'
                          }
                        />
                      ) : null}
                      <label className="section-caution-description">
                        Coupon stock left:{' '}
                        {storeSelectionType ===
                        StoreSelectionTypes.selectNow.value
                          ? targetStore?.couponStock ||
                            targetCouponTemplate?.stock ||
                            0
                          : targetCouponTemplate?.stock || 0}
                      </label>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <BaseMultipleSelectorV2
                    title={'Owned coupons'}
                    data={{
                      sourceData: ownedCoupons,
                      targetData: removedCoupons,
                      targetChange: (value) => {
                        setRemovedCoupons(value);
                      },
                    }}
                    groupKey="groupKey"
                  />
                </>
              )}
              <CustomTitleWithDropDown
                title="Related transaction (optional)"
                defaultValue={targetTransaction}
                setValue={setTargetTransaction}
                source={transactionList || []}
                customItem={(item) => {
                  return `Transaction ID: ${item.pk}`;
                }}
                showItemDescription={true}
                customItemDescription={(item) => {
                  return `Invoice ID: ${
                    (item.transactionDisplayType ===
                    TRANSACTION_RECORD_TYPE.TYPE_OFFLINE_POS
                      ? item.posInvoiceId
                      : item.invoiceId) || '-'
                  }`;
                }}
                loadMoreAction={'transactions/getPagedTransactionList'}
                filterAction={'transactions/getPagedTransactionList'}
                defaultFilter={{
                  ssoUid: customer?.ssoUid || targetCustomer?.ssoUid,
                  rank: true,
                  searchField: 'searchIdOrInvoiceId',
                }}
                isReset={lastTransactionRefreshDate !== transactionRefreshDate}
                resetChange={() => {
                  setLastTransactionRefreshDate(transactionRefreshDate);
                }}
                hideDeleteButton={true}
                placeholder={'Search by Transaction ID, Invoice ID'}
              />
            </>
          ) : null}

          {adjustType === ADJUST_TYPES.ticket ? (
            <>
              {purpose === PurposeTypes.add ? (
                <>
                  <CustomTitleWithDropDown
                    title="Select ticket campaign"
                    defaultValue={{
                      value: targetCampaign,
                      label: targetCampaign?.pk
                        ? `[ID: ${targetCampaign?.pk}] ${targetCampaign?.name}`
                        : targetCampaign?.name,
                    }}
                    setValue={(item) => {
                      setTargetCampaign(item?.value);
                      if (item?.value?.pk !== targetCampaign?.pk) {
                        const arrayLength =
                          (targetCampaign?.fillOtherInfomation &&
                            targetCampaign?.otherInformations?.length) ||
                          0;
                        setOtherInfomations(new Array(arrayLength));
                        setTargetCouponTemplate();
                        setLastCouponTemplateRefreshDate(new Date());
                        // dispatch(
                        //   createAction('couponList/getCurrentPageTemplateList')(
                        //     {
                        //       isSelectorLoad: true,
                        //       type: COUPON_TYPES.TICKET,
                        //       ticket_campaign: targetCampaign?.pk,
                        //       searchField: 'searchNameOrId',
                        //     },
                        //   ),
                        // );
                      }
                    }}
                    labelContainPk={true}
                    source={campaignList || []}
                    loadMoreAction={'campaignList/getCampaignList'}
                    filterAction={'campaignList/getCampaignList'}
                    defaultFilter={{
                      noLoading: true,
                      type: 'TICKET_CAMPAIGN',
                      ssoUid: customer?.ssoUid || targetCustomer?.ssoUid,
                      queryTicketOtherInformations: true,
                      reverse: true,
                      skipUpdatePagedData: true,
                      searchField: 'searchNameOrId',
                    }}
                    isReset={lastCampaignRefreshDate !== campaignRefreshDate}
                    resetChange={() => {
                      setLastCampaignRefreshDate(campaignRefreshDate);
                    }}
                    hideDeleteButton={true}
                    placeholder={'Search by ID,  name'}
                  />
                  {targetCampaign?.pk ? (
                    <>
                      <CustomTitleWithDropDown
                        customClass={'time-slot-selector'}
                        title="Time slot"
                        defaultValue={{
                          value: targetCouponTemplate,
                          label: targetCouponTemplate?.pk
                            ? `${
                                !targetCouponTemplate?.stock
                                  ? '[Out of stock] - '
                                  : ''
                              }[ID: ${targetCouponTemplate?.pk}] ${
                                targetCouponTemplate?.name
                              }`
                            : targetCouponTemplate?.name,
                        }}
                        setValue={(item) => {
                          setTargetCouponTemplate(item?.value);
                        }}
                        source={couponTemplateList || []}
                        customItem={(item) => {
                          return `${
                            !item.stock ? '[Out of stock] - ' : ''
                          }[ID: ${item.pk}] ${item.name}`;
                        }}
                        showItemDescription={true}
                        customItemDescription={(item) => {
                          return item.ticketStore?.storeAddress;
                        }}
                        customItemDisabled={(item) => !item.stock}
                        loadMoreAction={'couponList/getCurrentPageTemplateList'}
                        filterAction={'couponList/getCurrentPageTemplateList'}
                        defaultFilter={{
                          type: COUPON_TYPES.TICKET,
                          ticket_campaign: targetCampaign?.pk,
                          searchField: 'searchNameOrId',
                        }}
                        isReset={
                          lastCouponTemplateRefreshDate !==
                          couponTemplateRefreshDate
                        }
                        resetChange={() => {
                          setLastCouponTemplateRefreshDate(
                            couponTemplateRefreshDate,
                          );
                        }}
                        hideDeleteButton={true}
                        placeholder={'Search by ID,  name'}
                      />
                      <CustomTitleWithInput
                        title={'Ticket quantity'}
                        defaultValue={quantity}
                        setValue={(value) => {
                          setQuantity(value);
                        }}
                        type={'number'}
                        extra={{
                          min: 1,
                        }}
                        error={{ error: error?.quantity }}
                      />
                      {error.quantity ? (
                        <ErrorFieldMessage
                          id={'quantity'}
                          error={true}
                          message={
                            'The input value is larger than the number of coupon left, please try again.'
                          }
                        />
                      ) : null}
                      <label className="section-caution-description">
                        Ticket stock left: {targetCouponTemplate?.stock || 0}
                      </label>

                      {targetCampaign?.fillOtherInfomation &&
                      targetCampaign?.otherInformations?.length ? (
                        <>
                          <div className="line"></div>
                          <div className="other-information-container">
                            <CustomTitleLabel title="Other information" />
                            {targetCampaign?.otherInformations?.map(
                              (itemData, index) => {
                                return (
                                  <>
                                    <TicketOtherInformationField
                                      data={itemData}
                                      defaultValue={otherInformations[index]}
                                      setValue={(value) => {
                                        let newValue = [...otherInformations];
                                        newValue[index] = value;
                                        setOtherInfomations(newValue);
                                      }}
                                    />
                                  </>
                                );
                              },
                            )}
                          </div>
                          <div className="line"></div>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <BaseMultipleSelectorV2
                    title={'Select ticket'}
                    data={{
                      sourceData: ownedCoupons,
                      targetData: removedCoupons,
                      targetChange: (value) => {
                        setRemovedCoupons(value);
                      },
                    }}
                    groupKey="groupKey"
                  />
                </>
              )}
            </>
          ) : null}

          {adjustType === ADJUST_TYPES.stamp ? (
            <>
              {customer?.pk || !prefillData?.targetCampaign?.pk ? (
                <>
                  <CustomTitleWithDropDown
                    title="Stamp campaign"
                    defaultValue={{
                      value: targetCampaign,
                      label: targetCampaign?.pk
                        ? `[ID: ${targetCampaign?.pk}] ${targetCampaign?.name}`
                        : targetCampaign?.name,
                    }}
                    setValue={(item) => {
                      setTargetCampaign(item?.value);
                    }}
                    labelContainPk={true}
                    source={campaignList || []}
                    loadMoreAction={'campaignList/getCampaignList'}
                    filterAction={'campaignList/getCampaignList'}
                    defaultFilter={{
                      noLoading: true,
                      isStamp: true,
                      customerId:
                        purpose === PurposeTypes.add
                          ? ''
                          : customer?.pk || targetCustomer?.pk,
                      customerIdSkip:
                        purpose === PurposeTypes.add
                          ? customer?.pk || targetCustomer?.pk
                          : '',
                      ssoUid: customer?.ssoUid || targetCustomer?.ssoUid,
                      recurringCampaignExpiredByCustomerId: targetCustomer?.pk,
                      reverse: true,
                      skipUpdatePagedData: true,
                      searchField: 'searchNameOrId',
                    }}
                    isReset={lastCampaignRefreshDate !== campaignRefreshDate}
                    resetChange={() => {
                      setLastCampaignRefreshDate(campaignRefreshDate);
                    }}
                    // hideDeleteButton={true}
                    placeholder={'Search by stamp campaign ID and name'}
                  />
                </>
              ) : null}
              {targetCampaign?.pk ? (
                <>
                  <CustomTitleWithInput
                    title={'Stamp quantity'}
                    defaultValue={quantity}
                    setValue={(value) => {
                      setQuantity(value);
                    }}
                    type={'number'}
                    extra={{
                      min: 1,
                    }}
                    error={{ error: error?.quantity }}
                  />
                  {error.quantity ? (
                    <ErrorFieldMessage
                      id={'quantity'}
                      error={true}
                      message={`The input value is larger than the number of ${
                        purpose === PurposeTypes.add
                          ? 'stamps can add'
                          : 'currently owned'
                      }, please try again.`}
                    />
                  ) : null}
                  <label className="section-caution-description">
                    {purpose === PurposeTypes.add
                      ? `The maximum no. of stamps can add: ${
                          (targetCampaign?.maxRequiredStamps || 0) <
                          (targetCampaign?.totalStampsEarned || 0)
                            ? 0
                            : (targetCampaign?.maxRequiredStamps || 0) -
                              (targetCampaign?.totalStampsEarned || 0)
                        }`
                      : `Currently owned: ${
                          targetCampaign?.totalStampsEarned || 0
                        }`}
                  </label>
                </>
              ) : null}
              <CustomTitleWithDropDown
                title="Related transaction (optional)"
                defaultValue={targetTransaction}
                setValue={setTargetTransaction}
                source={transactionList || []}
                customItem={(item) => {
                  return `Transaction ID: ${item.pk}`;
                }}
                showItemDescription={true}
                customItemDescription={(item) => {
                  return `Invoice ID: ${
                    (item.transactionDisplayType ===
                    TRANSACTION_RECORD_TYPE.TYPE_OFFLINE_POS
                      ? item.posInvoiceId
                      : item.invoiceId) || '-'
                  }`;
                }}
                loadMoreAction={'transactions/getPagedTransactionList'}
                filterAction={'transactions/getPagedTransactionList'}
                defaultFilter={{
                  ssoUid: customer?.ssoUid || targetCustomer?.ssoUid,
                  rank: true,
                  searchField: 'searchIdOrInvoiceId',
                }}
                isReset={lastTransactionRefreshDate !== transactionRefreshDate}
                resetChange={() => {
                  setLastTransactionRefreshDate(transactionRefreshDate);
                }}
                hideDeleteButton={true}
                placeholder={'Search by Transaction ID, Invoice ID'}
              />
            </>
          ) : null}

          <CustomTitleLabel title="Admin remarks (optional)" />
          <AdjustRemarkTextarea remarks={remarks} setRemarks={setRemarks} />
        </div>
      </>
    );
  };

  const onClose = () => {
    if (showAddConfirm) {
      setShowAddConfirm(false);
      return;
    }
    if (confirm) {
      setConfirm(false);
      return;
    }
    setShow(false);
    setQuantity();
    setRemarks('');
  };

  const showSuccessToast = () => {
    dispatch({
      type: 'navBars/updateState',
      payload: {
        saveDiscardToastShowing: {
          value: true,
          type: ToastType.operatedSuccessfully,
          delayTime: defaultDelayTime,
        },
      },
    });
  };

  return (
    <BasePrompt
      customClass={'adjust-prompt-container'}
      show={show}
      closeAction={() => {
        onClose();
      }}
      leftButton={{
        text: 'Cancel',
        action: () => onClose(),
      }}
      rightButton={{
        text: 'Confirm',
        disabled: disabledButton,
        action: () => {
          setError({});
          setManualDisableButton(true);
          console.log('@values2', {
            targetCustomer,
            purpose,
            targetCouponTemplate,
            storeSelectionType,
            targetStore,
            quantity,
            removedCoupons,
            targetTransaction,
            targetCampaign,
            otherInformations,
            remarks,
          });

          if (!confirm) {
            if (adjustType === ADJUST_TYPES.coupon) {
              if (purpose === PurposeTypes.add) {
                const couponStock =
                  storeSelectionType === StoreSelectionTypes.selectNow.value
                    ? targetStore?.couponStock ||
                      targetCouponTemplate?.stock ||
                      0
                    : targetCouponTemplate?.stock || 0;
                if (!couponStock || couponStock < quantity) {
                  setError({ quantity: 'not valid' });
                  setManualDisableButton(false);
                  return;
                }
              }
            } else if (adjustType === ADJUST_TYPES.ticket) {
              if (purpose === PurposeTypes.add) {
                if (
                  !targetCouponTemplate?.stock ||
                  targetCouponTemplate?.stock < quantity
                ) {
                  setError({ quantity: 'not valid' });
                  setManualDisableButton(false);
                  return;
                }
              }
            } else if (adjustType === ADJUST_TYPES.stamp) {
              const maxQuantity =
                (purpose === PurposeTypes.add
                  ? targetCampaign?.maxRequiredStamps -
                    (targetCampaign?.totalStampsEarned || 0)
                  : targetCampaign?.totalStampsEarned) || 0;
              if (quantity > maxQuantity) {
                setError({ quantity: 'not valid' });
                setManualDisableButton(false);
                return;
              }
            }
            setConfirm(true);
            setManualDisableButton(false);
            return;
          }

          let requestData = {};
          if (adjustType === ADJUST_TYPES.coupon) {
            if (purpose === PurposeTypes.add) {
              requestData = {
                id: targetCustomer?.pk || null,
                couponsToAdjust: {
                  type: 'ADD_COUPONS',
                  value: quantity,
                  remarks: remarks,
                  templateId: targetCouponTemplate?.pk || null,
                  transactionId: targetTransaction?.value?.pk || null,
                  storeId:
                    storeSelectionType ===
                    StoreSelectionTypes.customerSelect.value
                      ? null
                      : targetStore?.pk || null,
                },
              };
            } else {
              requestData = {
                id: targetCustomer?.pk || null,
                couponsToAdjust: {
                  type: 'REMOVE_COUPONS',
                  value: quantity,
                  remarks: remarks,
                  couponIds: removedCoupons?.map((item) => item.pk),
                  transactionId: targetTransaction?.value?.pk || null,
                },
              };
            }
          } else if (adjustType === ADJUST_TYPES.ticket) {
            if (purpose === PurposeTypes.add) {
              let formatedOtherInformations = {};
              if (
                targetCampaign?.fillOtherInfomation &&
                targetCampaign?.otherInformations?.length
              ) {
                const temp = targetCampaign?.otherInformations?.forEach(
                  (itemData, index) => {
                    const { fieldFormat } = itemData;

                    let otherInformationValue = null;
                    if (
                      [
                        ADDITIONAL_FIELD_FORMAT_TYPE.TEXT,
                        ADDITIONAL_FIELD_FORMAT_TYPE.NUMBER,
                        ADDITIONAL_FIELD_FORMAT_TYPE.SINGLE_CHOICES,
                        ADDITIONAL_FIELD_FORMAT_TYPE.MULTI_CHOICES,
                      ].includes(fieldFormat)
                    ) {
                      otherInformationValue = otherInformations[index];
                    } else if (
                      [
                        CUSTOM_ADDITIONAL_FIELD_FORMAT_TYPE.DATE_MONTH,
                        ADDITIONAL_FIELD_FORMAT_TYPE.DATETIME,
                      ].includes(fieldFormat)
                    ) {
                      otherInformationValue = otherInformations[index];
                    } else if (
                      fieldFormat === ADDITIONAL_FIELD_FORMAT_TYPE.DROPDOWN
                    ) {
                      otherInformationValue =
                        otherInformations[index]?.value?.value;
                    } else if (
                      [
                        ADDITIONAL_FIELD_FORMAT_TYPE.TOGGLE,
                        ADDITIONAL_FIELD_FORMAT_TYPE.CHECKBOX,
                      ].includes(fieldFormat)
                    ) {
                      otherInformationValue = otherInformations[index]
                        ? true
                        : false;
                    }

                    formatedOtherInformations[itemData.infoPk] =
                      otherInformationValue;
                  },
                );
              }

              requestData = {
                id: targetCustomer?.pk || null,
                ticketToAdjust: {
                  type: 'ADD_TICKETS',
                  value: quantity,
                  remarks: remarks,
                  ticketCampaignId: targetCampaign?.pk || null,
                  couponTemplateId: targetCouponTemplate?.pk || null,
                  otherInfo: JSON.stringify(formatedOtherInformations),
                },
              };
            } else {
              requestData = {
                id: targetCustomer?.pk || null,
                ticketToAdjust: {
                  type: 'REMOVE_TICKETS',
                  value: quantity,
                  remarks: remarks,
                  couponIds: removedCoupons?.map((item) => item.pk),
                },
              };
            }
          } else if (adjustType === ADJUST_TYPES.stamp) {
            requestData = {
              id: targetCustomer?.pk || null,
              stampsToAdjust: {
                type:
                  purpose === PurposeTypes.add ? 'ADD_STAMPS' : 'REMOVE_STAMPS',
                value: quantity,
                remarks: remarks,
                stampCampaignId: targetCampaign?.pk || null,
                transactionId: targetTransaction?.value?.pk || null,
              },
            };
          }
          console.log('confirm', requestData);
          const adjustRewards = () =>
            dispatch(
              createAction('customerList/adjustCustomerRewards')({
                data: requestData,
                successCallback: () => {
                  setManualDisableButton(false);
                  setShow(false);
                  setRemarks('');
                  showSuccessToast();
                  history.push(location);
                  if (customer?.pk) {
                    dispatch({
                      type: 'customerList/getOneCustomer',
                      payload: { id: customer?.pk },
                    });
                  }
                },
                failureCallback: () => {
                  setManualDisableButton(false);
                },
              }),
            );
          if (
            purpose === PurposeTypes.add &&
            !showAddConfirm &&
            [ADJUST_TYPES.stamp, ADJUST_TYPES.coupon].includes(adjustType)
          ) {
            dispatch(
              createAction('customerList/adjustCustomerValidation')({
                customerId: targetCustomer?.pk || null,
                isAdd: true,
                adjustType: adjustType,
                value: quantity,
                validationSuccess: () => {
                  adjustRewards();
                },
                validationFailure: () => {
                  setShowAddConfirm(true);
                  setManualDisableButton(false);
                },
              }),
            );
            return;
          }
          adjustRewards();
        },
      }}
      title={
        showAddConfirm
          ? 'Reminder'
          : confirm
          ? 'Confirmation'
          : `Adjust ${adjustType}`
      }
      otherBody={otherBody}
    />
  );
}

export default AdjustPrompt;
